import React, { useState, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { FormField } from 'taboola-ultimate-ui';
import { useAvailableItems, useNavigate } from 'hooks';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CommonCollapsibleCard, ExternalLink } from '../../../components';
import { FormButtons } from '../../campaigns/components';
import { COMBINED_AUDIENCES_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/combined-audience/combinedAudiencesReportId';
import { CUSTOM_CONTEXTUAL_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/custom-contextual/customContextualReportId';
import { FIRST_PARTY_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/first-party/firstPartyReportId';
import { PREDICTIVE_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/lookalike/predictiveReportId';
import { MAIL_DOMAINS_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/mail-domains/mailDomainsReportId';
import { PIXEL_BASED_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/pixel-based/pixelBasedReportId';
import { SEARCH_KEYWORDS_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/search-keywords/searchReportsReportId';
import { useAccount } from '../../taboola-common-frontend-modules/account-configurations';
import { useModal } from '../../taboola-common-frontend-modules/modals';
import { AudienceCreationAdvancedOptions } from '../components/AudienceCreationAdvancedOptions/AudienceCreationAdvancedOptions';
import { AudienceCreationRadioIconGroup } from '../components/AudienceCreationRadioIconGroup/AudienceCreationRadioIconGroup';
import {
    moreAudienceConfigs,
    taboolaDataAudienceConfigs,
    yourDataAudienceConfigs,
} from '../config/audienceCreatorConfig';
import { useAudienceCreatorPixelPredictivePermissions } from '../hooks/useAudienceCreatorPixelPredictivePermissions';
import {
    generateFirstPartyAudienceCreatorPath,
    generatePredictiveAudienceCreatorPath,
    generatePixelBasedAudienceCreatorPath,
    generateCustomContextualCreatorPath,
    generateCombinedAudienceCreatorPath,
    generateMailDomainAudiencesCreatorPath,
    generateSearchKeywordsAudiencesCreatorPath,
} from '../utils/generateCreatorPath';
import styles from './audiencesCreatorSelectionPage.module.scss';

const paths = {
    [PIXEL_BASED_REPORT_ID]: generatePixelBasedAudienceCreatorPath(),
    [PREDICTIVE_REPORT_ID]: generatePredictiveAudienceCreatorPath(),
    [FIRST_PARTY_REPORT_ID]: generateFirstPartyAudienceCreatorPath(),
    [CUSTOM_CONTEXTUAL_REPORT_ID]: generateCustomContextualCreatorPath(),
    [MAIL_DOMAINS_REPORT_ID]: generateMailDomainAudiencesCreatorPath(),
    [SEARCH_KEYWORDS_REPORT_ID]: generateSearchKeywordsAudiencesCreatorPath(),
    [COMBINED_AUDIENCES_REPORT_ID]: generateCombinedAudienceCreatorPath(),
};

const yourDataLabel = <FormattedMessage id="audience.creator.selection.your.data" defaultMessage="Your data" />;
const taboolaDataLabel = (
    <FormattedMessage id="audience.creator.selection.taboola.data" defaultMessage="Taboola data" />
);
const moreOptionsLabel = (
    <FormattedMessage id="audience.creator.selection.more.options" defaultMessage="More options" />
);

const dataUsePolicyLink = (
    <ExternalLink href="https://www.taboola.com/policies/advertiser-data-use-policy" className={styles['link']}>
        <FormattedMessage
            id="audience.creator.selection.legal.disclaimer.link.text"
            defaultMessage="Taboola Advertiser Data Use Policy"
        />
    </ExternalLink>
);

export const AudiencesCreatorSelectionPage = () => {
    const [type, setType] = useState('pixel-audiences');
    const navigate = useNavigate();
    const { close: closeModal } = useModal();
    const handleNavigate = useCallback(async () => {
        await navigate(paths[type]);
        closeModal();
    }, [closeModal, navigate, type]);

    const { isPbpAllowed, isCrmLookalikeAllowed } = useAudienceCreatorPixelPredictivePermissions();

    const selectedAccount = useAccount();
    const conditionData = useMemo(
        () => ({ selectedAccount, isPbpAllowed, isCrmLookalikeAllowed }),
        [selectedAccount, isPbpAllowed, isCrmLookalikeAllowed]
    );

    const availableYourDataAudienceConfigs = useAvailableItems(yourDataAudienceConfigs, conditionData);
    const availableTaboolaDataAudienceConfigs = useAvailableItems(taboolaDataAudienceConfigs);
    const availableMoreAudienceConfigs = useAvailableItems(moreAudienceConfigs, conditionData);

    return (
        <div className={styles['container']}>
            <div className={styles['content-container']}>
                <div className={styles['form-container']}>
                    <FormattedMessage
                        id="audience.creator.selection.title"
                        tagName="h3"
                        defaultMessage="What do you want your audience to be based on?"
                    />
                    {!isEmpty(availableYourDataAudienceConfigs) && (
                        <FormField label={yourDataLabel}>
                            <AudienceCreationRadioIconGroup
                                type={type}
                                onTypeChange={setType}
                                audienceConfigs={availableYourDataAudienceConfigs}
                            />
                        </FormField>
                    )}
                    {!isEmpty(availableTaboolaDataAudienceConfigs) && (
                        <FormField label={taboolaDataLabel}>
                            <AudienceCreationRadioIconGroup
                                type={type}
                                onTypeChange={setType}
                                audienceConfigs={availableTaboolaDataAudienceConfigs}
                            />
                        </FormField>
                    )}
                    <CommonCollapsibleCard
                        id="CREATE_AUDIENCE_MORE_OPTIONS"
                        containerClassName={styles['more-options-container']}
                        headerClassName={styles['more-options-header']}
                        bodyClassName={styles['more-options-body']}
                        header={moreOptionsLabel}
                        isInitiallyCollapsed={true}
                    >
                        {!isEmpty(availableMoreAudienceConfigs) && (
                            <AudienceCreationRadioIconGroup
                                type={type}
                                onTypeChange={setType}
                                audienceConfigs={availableMoreAudienceConfigs}
                            />
                        )}
                        <AudienceCreationAdvancedOptions />
                    </CommonCollapsibleCard>
                </div>
            </div>
            <div className={styles['buttons-container']}>
                <FormattedMessage
                    className={styles['disclaimer']}
                    id="audience.creator.selection.legal.disclaimer"
                    defaultMessage="By continuing, you agree that you have read and will comply with the {link} in the creation of new Audiences."
                    values={{ link: dataUsePolicyLink }}
                />
                <FormButtons
                    className={styles['buttons']}
                    onCancel={closeModal}
                    disableSubmit={!type}
                    submitLabel={<FormattedMessage id="app.actionButtons.continue" defaultMessage="Continue" />}
                    onSubmit={handleNavigate}
                />
            </div>
        </div>
    );
};
